import {ucFirst} from '../../src/utils/strings.ts';
import {createElement} from './dom.ts';

export class LayoutParser {
	private readonly document: Document;
	private el: HTMLDivElement;

	constructor(markup: string, document: Document) {
		this.document = document;
		this.el = createElement(document, 'div') as HTMLDivElement;
		this.el.innerHTML = markup;
	}

	getHeadline(): HTMLElement | undefined {
		const headlineEl =
			(this.el.querySelector('.headline') as HTMLElement) || (this.el.querySelector('h3') as HTMLElement);
		if (!headlineEl) {
			return this.getIntroAsHeadline();
		}
		const h1AsP = headlineEl.querySelector<HTMLElement>('h1:empty + p');
		if (h1AsP) {
			const parentNode = h1AsP.parentNode as HTMLElement;
			const h1El = createElement(this.document, 'h1');
			h1El.innerHTML = h1AsP.innerHTML;
			parentNode.replaceWith(h1El);
			let nextSibling = h1AsP.nextElementSibling;
			while (nextSibling) {
				const newEl = createElement(this.document, 'h1') as HTMLElement;
				newEl.innerHTML = nextSibling.innerHTML;
				h1El.insertAdjacentElement('afterend', newEl);
				nextSibling = nextSibling.nextElementSibling;
			}
		}

		return headlineEl;
	}

	getIntroAsHeadline(): HTMLElement | undefined {
		return this.el.querySelector('.headline--ingresstittel') as HTMLElement;
	}

	getKicker(): HTMLElement | undefined {
		return this.el.querySelector('.kicker') as HTMLElement;
	}

	getByline() {
		const bylineEl = this.el.querySelector('div.byline');
		if (!bylineEl) {
			return;
		}

		// This is a quite rudimentary approach to clean up byline elements with no content
		const children = Array.from(bylineEl.childNodes) as HTMLElement[];
		children.forEach((childEl) => {
			if (childEl.innerText?.trim() === '') {
				childEl.remove();
				return;
			}
			const innerChildren = Array.from(childEl.childNodes) as HTMLElement[];
			innerChildren.forEach((innerChildEl) => {
				if (innerChildEl.innerText?.trim() === '' && !innerChildEl?.querySelector('img')) {
					innerChildEl.remove();
				}
			});
		});

		// console.log(bylineEl);

		const el = createElement(bylineEl.ownerDocument, 'div');
		const bylineImageEl = this.el.querySelector('div.byline-image');
		if (bylineImageEl) {
			const currentImgEl = bylineEl.querySelector('img');
			const newImgEl = bylineImageEl.querySelector('img');
			if (currentImgEl && newImgEl) {
				currentImgEl.replaceWith(newImgEl);
			}
		}
		el.appendChild(bylineEl);
		// console.log(el.innerHTML);
		return el.innerHTML;
	}

	getFirstAsset() {
		const firstByIndex = this.el.querySelector('.picture[data-index="1"]');
		if (firstByIndex) {
			return firstByIndex;
		}

		return this.el.querySelector('.picture');
	}

	getFirstCaption() {
		const firstByIndex = this.el.querySelector('.caption[data-index="1"]');
		if (firstByIndex) {
			return firstByIndex;
		}

		return this.el.querySelector('.caption');
	}

	getText(el: HTMLElement | undefined, selector: string) {
		if (!el) {
			return '';
		}
		return Array.from<HTMLElement>(el.querySelectorAll(selector))
			.map((el) => el.innerHTML)
			.join(' ');
	}

	getSection() {
		const pageTopEl = this.el.querySelector<HTMLElement>('.page-top--section');
		if (!pageTopEl) {
			return '';
		}

		return pageTopEl.innerText;
	}

	getStory() {
		const pageTopEl = this.el.querySelector<HTMLElement>('.page-top--story');
		if (pageTopEl) {
			return pageTopEl.innerText;
		}

		const vignetteEl = this.el.querySelector<HTMLElement>('.page-top + .vignette, [data-component="vignette"]');
		if (vignetteEl) {
			return vignetteEl.innerText;
		}

		const topicEl = this.el.querySelector<HTMLElement>('.topic');
		if (topicEl) {
			return topicEl.innerText;
		}

		const introFirstSentenceEl = this.el.querySelector<HTMLElement>('.intro .first-sentence');
		if (introFirstSentenceEl) {
			return ucFirst(introFirstSentenceEl.innerText.split(':')[0].toLocaleLowerCase());
		}

		return '';
	}

	getFirstParagraphLeadin() {
		const firstParagraphEl = this.el.querySelector<HTMLParagraphElement>('article > p');
		if (firstParagraphEl) {
			const parts = firstParagraphEl.innerText.split(':');
			if (parts.length > 1) {
				return ucFirst(parts[0].toLocaleLowerCase());
			}
		}

		return '';
	}

	getFirstWordLeadin() {
		const firstParagraphEl = this.el.querySelector<HTMLParagraphElement>('article > p.body_leadin');
		if (firstParagraphEl) {
			const parts = firstParagraphEl.innerText.split('.');
			if (parts.length > 1) {
				return ucFirst(parts[0].toLocaleLowerCase());
			}
		}

		return '';
	}
}
